/*
 *   File : create-kyc.js
 *   Author URI : www.evoqins.com
 *   Description : Create KYC request
 *   Integrations : null
 *   Version : v1.0
 *   Created : 02-07-2024
 */

// import package
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "bootstrap";

// import components
import { GradientButton, OutlineButton } from "../../Components/Cta";
import { NavigationHeader } from "../../Components/Header";
import { SectionTitle } from "../../Components/Title";
import { _uploadDocumentToS3 } from "../../Services/aws-service";

import { CustomTextInput, CustomPhoneInput, FileUpload } from "../../Components/FormElements";
import { PreviewModal, SuccessModal } from "../../Components/Modal";

// import api services
import { _getAdminTypes, _getPermissions, _getZone } from "../../Helper/api";
import APIService from "../../Services/api-service";
import { FilePreview } from "../../Components/Cards";
import { Icon } from "../../Components/Icon";

// asset
import PreviewImage from '../../Assets/Images/White-Label-Settings/marketing.avif';
import { CustomLoader } from "../../Components/Other";


export default function CreateNewList() {

    const navigate = useNavigate();
    const { state } = useLocation();
    console.log('manage_state', state);

    const [distributorName, setDistributorName] = useState("");
    const [distributorNameError, setDistributorNameError] = useState("");

    const [domainUrl, setDomainUrl] = useState("");
    const [domainUrlError, setDomainUrlError] = useState("");

    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneNumberError, setPhoneNumberError] = useState("");

    const [footerLogoText, setFooterLogoText] = useState("");
    const [footerLogoTextError, setFooterLogoTextError] = useState("");

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");

    // Light theme logo states
    const [headerFileName, setHeaderFileName] = useState(null);
    const [footerFileName, setFooterFileName] = useState(null);
    const [headerFileSize, setHeaderFileSize] = useState("");
    const [footerFileSize, setFooterFileSize] = useState("");
    const [headerFileError, setHeaderFileError] = useState(null);
    const [footerFileError, setFooterFileError] = useState(null);
    const [headerFileUrl, setHeaderFileUrl] = useState('');
    const [footerFileUrl, setFooterFileUrl] = useState('');
    const [headerFileLoader, setHeaderFileLoader] = useState(false);
    const [footerFileLoader, setFooterFileLoader] = useState(false);

    // Dark theme logo states
    const [darkHeaderFileName, setDarkHeaderFileName] = useState(null);
    const [darkFooterFileName, setDarkFooterFileName] = useState(null);
    const [darkHeaderFileSize, setDarkHeaderFileSize] = useState("");
    const [darkFooterFileSize, setDarkFooterFileSize] = useState("");
    const [darkHeaderFileError, setDarkHeaderFileError] = useState(null);
    const [darkFooterFileError, setDarkFooterFileError] = useState(null);
    const [darkHeaderFileUrl, setDarkHeaderFileUrl] = useState('');
    const [darkFooterFileUrl, setDarkFooterFileUrl] = useState('');
    const [darkHeaderFileLoader, setDarkHeaderFileLoader] = useState(false);
    const [darkFooterFileLoader, setDarkFooterFileLoader] = useState(false);

    const [btnLoader, setBtnLoader] = useState(false);
    const [previewModal, setPreviewModal] = useState(false);
    const [successModal, setSuccessModal] = useState(false);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        });
    }, []);

    useEffect(() => {
        if (state !== null) {
            setDistributorName(state.details.distributor_name);
            setDomainUrl(state.details.domain_url);
            setEmail(state.details.email);
            setPhoneNumber(state.details.phone);
            setFooterLogoText(state.details.branding_assets.footer_logo_text);

            // Light theme logos
            setHeaderFileUrl(state.details.branding_assets.header_logo_url);
            setFooterFileUrl(state.details.branding_assets.footer_logo_url);
            setHeaderFileName(state.details.branding_assets.header_logo_file_name);
            setFooterFileName(state.details.branding_assets.footer_logo_file_name);

            // Dark theme logos
            setDarkHeaderFileUrl(state.details.branding_assets.dark_header_logo);
            setDarkFooterFileUrl(state.details.branding_assets.dark_footer_logo);
            setDarkHeaderFileName(state.details.branding_assets.dark_header_file_name);
            setDarkFooterFileName(state.details.branding_assets.dark_footer_file_name);

            // Helper function to format file size
            const formatFileSize = (fileSizeInBytes) => {
                const fileSizeInKB = (fileSizeInBytes / 1024).toFixed(2);
                const fileSizeInMB = (fileSizeInBytes / (1024 * 1024)).toFixed(2);
                return fileSizeInBytes < 1024 * 1024
                    ? `${fileSizeInKB} KB`
                    : `${fileSizeInMB} MB`;
            };

            setHeaderFileSize(formatFileSize(state.details.branding_assets.header_logo_file_size));
            setFooterFileSize(formatFileSize(state.details.branding_assets.footer_logo_file_size));
            setDarkHeaderFileSize(formatFileSize(state.details.branding_assets.dark_header_file_size));
            setDarkFooterFileSize(formatFileSize(state.details.branding_assets.dark_footer_file_size));
        }
    }, [state]);

    useEffect(() => {
        if (successModal === true) {
            const success_modal = new Modal(document.getElementById("success-modal"));
            success_modal.show();
            setBtnLoader(false);
        }
    }, [successModal]);

    useEffect(() => {
        if (previewModal === true) {
            const preview_modal = new Modal(document.getElementById("preview-modal"));
            preview_modal.show();
        }
    }, [previewModal]);

    // navigate to listing
    function _navigateToDetail() {
        navigate('/white-label-settings/detail', {
            state: {
                detail_id: state.details.id
            }
        });
    };

    // handle input changes
    function _handleNameInput(value) {
        setDistributorName(value);
        setDistributorNameError("");
    };

    function _handleDomainInput(value) {
        setDomainUrl(value);
        setDomainUrlError("");
    };

    function _handlePhoneInput(value) {
        setPhoneNumber(value);
        setPhoneNumberError("");
    };

    function _handleFooterLogoTextInput(value) {
        setFooterLogoText(value);
        setFooterLogoTextError("");
    };

    function _handleEmailInput(value) {
        setEmail(value);
        setEmailError("");
    };

    function _scrollToError(element_id) {
        const element = document.getElementById(element_id);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            element.focus();
        }
    };

    function _validateForm() {
        let valid_form = true;
        let first_error_id = null;

        const url_pattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?(\/.*)?$/;
        const domain_pattern = /\.(com|org|net|edu|gov|co|io|ai|app|dev|tech|info|biz|in|uk|us|ca|au)(\.[a-zA-Z]{2,})?(\/.*)?$/i;

        if (distributorName.trim().length === 0) {
            setDistributorNameError("Distributor Name is required");
            valid_form = false;
            if (!first_error_id) first_error_id = "distributor-name-input";
        }

        if (domainUrl.trim().length === 0) {
            setDomainUrlError("Domain Url is required");
            valid_form = false;
            if (!first_error_id) first_error_id = "domain-url-input";
        } else {
            if (!url_pattern.test(domainUrl.trim()) || !domain_pattern.test(domainUrl.trim())) {
                setDomainUrlError("Invalid domain URL format. Must include a valid domain extension (e.g., .com, .org, .net)");
                valid_form = false;
                if (!first_error_id) first_error_id = "domain-url-input";
            }
        }

        if (footerLogoText.trim().length === 0) {
            setFooterLogoTextError("Footer Logo Text is required");
            valid_form = false;
            if (!first_error_id) first_error_id = "footer-logo-text-input";
        }

        if (phoneNumber === "") {
            setPhoneNumberError('Phone number is required');
            valid_form = false;
            if (!first_error_id) first_error_id = "phone-number-input";
        } else if (phoneNumber.length !== 10) {
            setPhoneNumberError("Invalid phone number");
            valid_form = false;
            if (!first_error_id) first_error_id = "phone-number-input";
        }

        if (email === "") {
            setEmailError("Email is required");
            valid_form = false;
            if (!first_error_id) first_error_id = "email-input";
        } else if (!/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
            setEmailError("Invalid Email");
            valid_form = false;
            if (!first_error_id) first_error_id = "email-input";
        }

        // Light theme logo validation
        if (!headerFileUrl || headerFileUrl.trim().length === 0) {
            setHeaderFileError("Header logo is required");
            valid_form = false;
            if (!first_error_id) first_error_id = "header-logo-input";
        }

        // if (!footerFileUrl || footerFileUrl.trim().length === 0) {
        //     setFooterFileError("Footer logo is required");
        //     valid_form = false;
        //     if (!first_error_id) first_error_id = "footer-logo-input";
        // }

        // // Dark theme logo validation
        // if (!darkHeaderFileUrl || darkHeaderFileUrl.trim().length === 0) {
        //     setDarkHeaderFileError("Dark theme header logo is required");
        //     valid_form = false;
        //     if (!first_error_id) first_error_id = "dark-header-logo-input";
        // }

        // if (!darkFooterFileUrl || darkFooterFileUrl.trim().length === 0) {
        //     setDarkFooterFileError("Dark theme footer logo is required");
        //     valid_form = false;
        //     if (!first_error_id) first_error_id = "dark-footer-logo-input";
        // }

        if (valid_form === true) {
            _manageDistributor();
        } else {
            if (first_error_id) {
                setTimeout(() => _scrollToError(first_error_id), 100);
            }
        }
    };

    // UPLOAD FUNCTIONS
    const _headerFileUpload = (key, file) => {
        setHeaderFileLoader(true);
        let url = "general/file-upload";
        let body = {
            "key": `banner/header/${key}`,
            is_public: true
        }

        APIService(true, url, body).then(async (response) => {
            if (response.status_code === 200) {
                await _uploadDocumentToS3(response.data, key, file).then((result) => {
                    setHeaderFileUrl(response.data.view_info);
                    setHeaderFileName(key);
                    setHeaderFileLoader(false);
                }).catch(error => {
                    toast.dismiss();
                    toast.error(error, { type: "error" });
                    setHeaderFileLoader(false);
                });
            } else {
                setHeaderFileLoader(false);
                toast.dismiss();
                toast.error(response, { type: "error" });
            }
        })
    };

    const _footerFileUpload = (key, file) => {
        setFooterFileLoader(true);
        let url = "general/file-upload";
        let body = {
            "key": `banner/footer/${key}`,
            is_public: true
        }

        APIService(true, url, body).then(async (response) => {
            if (response.status_code === 200) {
                await _uploadDocumentToS3(response.data, key, file).then((result) => {
                    setFooterFileUrl(response.data.view_info);
                    setFooterFileName(key);
                    setFooterFileLoader(false);
                }).catch(error => {
                    toast.dismiss();
                    toast.error(error, { type: "error" });
                    setFooterFileLoader(false);
                });
            } else {
                setFooterFileLoader(false);
                toast.dismiss();
                toast.error(response, { type: "error" });
            }
        })
    };

    const _darkHeaderFileUpload = (key, file) => {
        setDarkHeaderFileLoader(true);
        let url = "general/file-upload";
        let body = {
            "key": `banner/dark-header/${key}`,
            is_public: true
        }

        APIService(true, url, body).then(async (response) => {
            if (response.status_code === 200) {
                await _uploadDocumentToS3(response.data, key, file).then((result) => {
                    setDarkHeaderFileUrl(response.data.view_info);
                    setDarkHeaderFileName(key);
                    setDarkHeaderFileLoader(false);
                }).catch(error => {
                    toast.dismiss();
                    toast.error(error, { type: "error" });
                    setDarkHeaderFileLoader(false);
                });
            } else {
                setDarkHeaderFileLoader(false);
                toast.dismiss();
                toast.error(response, { type: "error" });
            }
        })
    };

    const _darkFooterFileUpload = (key, file) => {
        setDarkFooterFileLoader(true);
        let url = "general/file-upload";
        let body = {
            "key": `banner/dark-footer/${key}`,
            is_public: true
        }

        APIService(true, url, body).then(async (response) => {
            if (response.status_code === 200) {
                await _uploadDocumentToS3(response.data, key, file).then((result) => {
                    setDarkFooterFileUrl(response.data.view_info);
                    setDarkFooterFileName(key);
                    setDarkFooterFileLoader(false);
                }).catch(error => {
                    toast.dismiss();
                    toast.error(error, { type: "error" });
                    setDarkFooterFileLoader(false);
                });
            } else {
                setDarkFooterFileLoader(false);
                toast.dismiss();
                toast.error(response, { type: "error" });
            }
        })
    };

    function _verifyImageFileType(fileType) {
        const valid_mime_type = [
            "image/png",
            "image/jpeg",
            "image/svg+xml"
        ];
        return valid_mime_type.includes(fileType);
    }

    function _headerFileHandler(path, file) {
        var file_formatted = path.replace(/^.*[\\\/]/, '').split('/');
        const formatted_file_name = file_formatted[0].replace(/[\(\)\s]/g, '');
        const file_type = file.type;
        const file_size = file.size;
        const max_size = 1 * 1024 * 1024;

        if (!_verifyImageFileType(file_type)) {
            toast.dismiss();
            toast.error("File format should be PNG, JPG, or SVG", { type: "error" });
            setHeaderFileError("Invalid file format");
            return;
        }

        if (file_size > max_size) {
            toast.dismiss();
            toast.error("File size should not exceed 1 MB", { type: "error" });
            setHeaderFileError("File size exceeds 1 MB");
            return;
        }

        const fileSizeInKB = (file_size / 1024).toFixed(2);
        const fileSizeInMB = (file_size / (1024 * 1024)).toFixed(2);
        const formattedSize = file_size < 1024 * 1024 ? `${fileSizeInKB} KB` : `${fileSizeInMB} MB`;

        const reader = new FileReader();
        reader.onload = (e) => {
            setHeaderFileUrl(e.target.result);
            setHeaderFileName(file.name);
            setHeaderFileSize(formattedSize);
            setHeaderFileError("");
            _headerFileUpload(formatted_file_name, file);
        };
        reader.readAsDataURL(file);
    };

    function _footerFileHandler(path, file) {
        var file_formatted = path.replace(/^.*[\\\/]/, '').split('/');
        const formatted_file_name = file_formatted[0].replace(/[\(\)\s]/g, '');
        const file_type = file.type;
        const file_size = file.size;
        const max_size = 1 * 1024 * 1024;

        if (!_verifyImageFileType(file_type)) {
            toast.dismiss();
            toast.error("File format should be PNG, JPG, or SVG", { type: "error" });
            setFooterFileError("Invalid file format");
            return;
        }

        if (file_size > max_size) {
            toast.dismiss();
            toast.error("File size should not exceed 1 MB", { type: "error" });
            setFooterFileError("File size exceeds 1 MB");
            return;
        }

        const fileSizeInKB = (file_size / 1024).toFixed(2);
        const fileSizeInMB = (file_size / (1024 * 1024)).toFixed(2);
        const formattedSize = file_size < 1024 * 1024 ? `${fileSizeInKB} KB` : `${fileSizeInMB} MB`;

        const reader = new FileReader();
        reader.onload = (e) => {
            setFooterFileUrl(e.target.result);
            setFooterFileName(file.name);
            setFooterFileSize(formattedSize);
            setFooterFileError("");
            _footerFileUpload(formatted_file_name, file);
        };
        reader.readAsDataURL(file);
    };

    function _darkHeaderFileHandler(path, file) {
        var file_formatted = path.replace(/^.*[\\\/]/, '').split('/');
        const formatted_file_name = file_formatted[0].replace(/[\(\)\s]/g, '');
        const file_type = file.type;
        const file_size = file.size;
        const max_size = 1 * 1024 * 1024;

        if (!_verifyImageFileType(file_type)) {
            toast.dismiss();
            toast.error("File format should be PNG, JPG, or SVG", { type: "error" });
            setDarkHeaderFileError("Invalid file format");
            return;
        }

        if (file_size > max_size) {
            toast.dismiss();
            toast.error("File size should not exceed 1 MB", { type: "error" });
            setDarkHeaderFileError("File size exceeds 1 MB");
            return;
        }

        const fileSizeInKB = (file_size / 1024).toFixed(2);
        const fileSizeInMB = (file_size / (1024 * 1024)).toFixed(2);
        const formattedSize = file_size < 1024 * 1024 ? `${fileSizeInKB} KB` : `${fileSizeInMB} MB`;

        const reader = new FileReader();
        reader.onload = (e) => {
            setDarkHeaderFileUrl(e.target.result);
            setDarkHeaderFileName(file.name);
            setDarkHeaderFileSize(formattedSize);
            setDarkHeaderFileError("");
            _darkHeaderFileUpload(formatted_file_name, file);
        };
        reader.readAsDataURL(file);
    };

    function _darkFooterFileHandler(path, file) {
        var file_formatted = path.replace(/^.*[\\\/]/, '').split('/');
        const formatted_file_name = file_formatted[0].replace(/[\(\)\s]/g, '');
        const file_type = file.type;
        const file_size = file.size;
        const max_size = 1 * 1024 * 1024;

        if (!_verifyImageFileType(file_type)) {
            toast.dismiss();
            toast.error("File format should be PNG, JPG, or SVG", { type: "error" });
            setDarkFooterFileError("Invalid file format");
            return;
        }

        if (file_size > max_size) {
            toast.dismiss();
            toast.error("File size should not exceed 1 MB", { type: "error" });
            setDarkFooterFileError("File size exceeds 1 MB");
            return;
        }

        const fileSizeInKB = (file_size / 1024).toFixed(2);
        const fileSizeInMB = (file_size / (1024 * 1024)).toFixed(2);
        const formattedSize = file_size < 1024 * 1024 ? `${fileSizeInKB} KB` : `${fileSizeInMB} MB`;

        const reader = new FileReader();
        reader.onload = (e) => {
            setDarkFooterFileUrl(e.target.result);
            setDarkFooterFileName(file.name);
            setDarkFooterFileSize(formattedSize);
            setDarkFooterFileError("");
            _darkFooterFileUpload(formatted_file_name, file);
        };
        reader.readAsDataURL(file);
    };

    const _manageDistributor = () => {
        setBtnLoader(true);
        let is_post = true;
        let url = 'white-label/Add-distributor';
        let payload = {
            "id": state === null ? null : state.details.id,
            "distributor_name": distributorName,
            "domain_url": domainUrl,
            "email": email,
            "phone": phoneNumber,
            "header_logo_url": headerFileUrl,
            "footer_logo_url": footerFileUrl,
            "dark_theme_header_logo": darkHeaderFileUrl,
            "dark_theme_footer_logo": darkFooterFileUrl,
            "footer_logo_text": footerLogoText
        }
        APIService(is_post, url, payload).then((response) => {
            if (response.status_code === 200) {
                setPreviewModal(true);
            } else {
                toast.dismiss();
                toast.error(response.message);
            }
            setBtnLoader(false);
        })
    };

    return (
        <>
            <NavigationHeader type={1} title="White Label Settings" />

            <div className="ps-4 pe-lg-5 pe-md-2 pe-2 ">
                <div className="row padding-32px-bottom">
                    <div className="col-12 d-flex justify-content-between">
                        <SectionTitle title={state !== null ? 'Edit distributor' : "Add new distributor"} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-6">
                        <div className="row">
                            <div id="distributor-name-input" className="col-12">
                                <CustomTextInput label="Distributor name"
                                    value={distributorName}
                                    postfix="*"
                                    id="name-input"
                                    type="text"
                                    error={distributorNameError}
                                    onEnter={_validateForm}
                                    valueChange={_handleNameInput} />
                            </div>

                            <div id="domain-url-input" className="col-12 mt-4 pt-3">
                                <CustomTextInput label="Domain URL"
                                    value={domainUrl}
                                    postfix="*"
                                    id="name-input"
                                    type="text"
                                    error={domainUrlError}
                                    onEnter={_validateForm}
                                    valueChange={_handleDomainInput} />
                            </div>

                            <div id="email-input" className="col-12 mt-4 pt-3">
                                <CustomTextInput label="Email"
                                    value={email}
                                    postfix="*"
                                    id="email-input"
                                    type="text"
                                    error={emailError}
                                    onEnter={_validateForm}
                                    valueChange={_handleEmailInput} />
                            </div>

                            <div id="phone-number-input" className="col-12 mt-4 pt-3">
                                <CustomPhoneInput label="Phone"
                                    value={phoneNumber}
                                    postfix="*"
                                    id="phone-input"
                                    type="text"
                                    error={phoneNumberError}
                                    handleChange={_handlePhoneInput} />
                            </div>

                            {/* Light Theme Header Logo */}
                            <div className="col-12 mt-4 pt-3">
                                <h3 className="e-font-16 e-poppins-medium line-height-16px color-black mb-3">
                                    Upload header logo (Light theme)
                                </h3>
                                <div id="header-logo-input" className="col-6">
                                    {!headerFileUrl ?
                                        <FileUpload
                                            id="header-upload"
                                            error={headerFileError}
                                            loading={headerFileLoader}
                                            accept=".png, .jpg, .jpeg, .svg"
                                            type={1}
                                            hide_error_text={false}
                                            title="Select a file"
                                            content="Supports: .PNG, .JPG, .SVG | Max size: 1 MB"
                                            onSelectFile={(path, file) => {
                                                _headerFileHandler(path, file)
                                            }}
                                        />
                                        :
                                        <FilePreview
                                            fileName={headerFileName}
                                            showRedDeleteIcon={true}
                                            fileSize={headerFileSize}
                                            icon={headerFileUrl}
                                            type={2}
                                            clearFile={() => {
                                                setHeaderFileName("");
                                                setHeaderFileUrl("");
                                                setHeaderFileSize("");
                                                setHeaderFileError("");
                                            }}
                                        />
                                    }
                                </div>
                            </div>

                            {/* Light Theme Footer Logo */}
                            <div className="col-12 mt-4 pt-3">
                                <h3 className="e-font-16 e-poppins-medium line-height-16px color-black mb-3">
                                    Upload footer logo (Light theme)
                                </h3>
                                <div id="footer-logo-input" className="col-6">
                                    {!footerFileUrl ?
                                        <FileUpload id="footer-upload"
                                            error={footerFileError}
                                            loading={footerFileLoader}
                                            accept=".png, .jpg, .jpeg, .svg"
                                            type={1}
                                            hide_error_text={false}
                                            title="Select a file"
                                            content="Supports: .PNG, .JPG, .SVG | Max size: 1 MB"
                                            onSelectFile={(file, path) => {
                                                _footerFileHandler(file, path)
                                            }}
                                        />
                                        :
                                        <FilePreview fileName={footerFileName}
                                            showRedDeleteIcon={true}
                                            fileSize={footerFileSize}
                                            icon={footerFileUrl}
                                            type={2}
                                            clearFile={() => {
                                                setFooterFileName("");
                                                setFooterFileUrl("");
                                                setFooterFileSize("");
                                                setFooterFileError("");
                                            }} />
                                    }
                                </div>
                            </div>

                            {/* Dark Theme Header Logo */}
                            <div className="col-12 mt-4 pt-3">
                                <h3 className="e-font-16 e-poppins-medium line-height-16px color-black mb-3">
                                    Upload header logo (Dark theme)
                                </h3>
                                <div id="dark-header-logo-input" className="col-6">
                                    {!darkHeaderFileUrl ?
                                        <FileUpload
                                            id="dark-header-upload"
                                            error={darkHeaderFileError}
                                            loading={darkHeaderFileLoader}
                                            accept=".png, .jpg, .jpeg, .svg"
                                            type={1}
                                            hide_error_text={false}
                                            title="Select a file"
                                            content="Supports: .PNG, .JPG, .SVG | Max size: 1 MB"
                                            onSelectFile={(path, file) => {
                                                _darkHeaderFileHandler(path, file)
                                            }}
                                        />
                                        :
                                        <FilePreview
                                            fileName={darkHeaderFileName}
                                            showRedDeleteIcon={true}
                                            fileSize={darkHeaderFileSize}
                                            icon={darkHeaderFileUrl}
                                            type={2}
                                            clearFile={() => {
                                                setDarkHeaderFileName("");
                                                setDarkHeaderFileUrl("");
                                                setDarkHeaderFileSize("");
                                                setDarkHeaderFileError("");
                                            }}
                                        />
                                    }
                                </div>
                            </div>

                            {/* Dark Theme Footer Logo */}
                            <div className="col-12 mt-4 pt-3">
                                <h3 className="e-font-16 e-poppins-medium line-height-16px color-black mb-3">
                                    Upload footer logo (Dark theme)
                                </h3>
                                <div id="dark-footer-logo-input" className="col-6">
                                    {!darkFooterFileUrl ?
                                        <FileUpload id="dark-footer-upload"
                                            error={darkFooterFileError}
                                            loading={darkFooterFileLoader}
                                            accept=".png, .jpg, .jpeg, .svg"
                                            type={1}
                                            hide_error_text={false}
                                            title="Select a file"
                                            content="Supports: .PNG, .JPG, .SVG | Max size: 1 MB"
                                            onSelectFile={(file, path) => {
                                                _darkFooterFileHandler(file, path)
                                            }}
                                        />
                                        :
                                        <FilePreview fileName={darkFooterFileName}
                                            showRedDeleteIcon={true}
                                            fileSize={darkFooterFileSize}
                                            icon={darkFooterFileUrl}
                                            type={2}
                                            clearFile={() => {
                                                setDarkFooterFileName("");
                                                setDarkFooterFileUrl("");
                                                setDarkFooterFileSize("");
                                                setDarkFooterFileError("");
                                            }} />
                                    }
                                </div>
                            </div>

                            <div id="footer-logo-text-input" className="col-12 mt-4 pt-3">
                                <CustomTextInput label="Footer Logo text"
                                    value={footerLogoText}
                                    postfix="*"
                                    id="name-input"
                                    type="text"
                                    error={footerLogoTextError}
                                    onEnter={_validateForm}
                                    valueChange={_handleFooterLogoTextInput} />
                            </div>

                        </div>

                        <div className="row">
                            <div className="col-12 mt-4 pt-3">
                                <OutlineButton label="Cancel"
                                    onPress={_navigateToDetail}
                                    className="me-3 color-black bg-white padding-72px-lr" />
                                <GradientButton label={state !== null ? 'Save' : "Preview"}
                                    className="padding-60px-lr"
                                    loading={btnLoader}
                                    onPress={_validateForm} />
                            </div>
                        </div>

                    </div>

                    {
                        state === null &&
                        <div className="col-lg-6">
                            <div className="d-flex ">
                                <div className=" d-flex flex-column  justify-content-between align-items-end">
                                    <div>
                                        <div className="d-flex align-items-end justify-content-center pb-2">
                                            <Icon icon='right-icon' className="ms-1" size={16} />
                                            <h4 className="e-font-14 e-poppins-medium line-height-14px color-gray mb-0">Distributor name</h4>
                                        </div>
                                        <div className="d-flex align-items-end justify-content-center ">
                                            <Icon icon='right-icon' className="ms-1" size={16} />
                                            <h4 className="e-font-14 e-poppins-medium line-height-14px color-gray mb-0">Header logo</h4>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-end justify-content-center padding-76px-bottom ">
                                        <Icon icon='right-icon' className="ms-1" size={16} />
                                        <h4 className="e-font-14 e-poppins-medium line-height-14px color-gray mb-0">Footer logo</h4>
                                    </div>
                                </div>
                                <div className="ms-2">
                                    <img className="object-fit-cover " src={require('../../Assets/Images/White-Label-Settings/preview-display.png')} width={353} height={304} alt="preview display" />
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>

            {
                previewModal === true &&
                <PreviewModal
                    previewImage={PreviewImage}
                    btnLoader={btnLoader}
                    headerLogoIcon={headerFileUrl}
                    headerLogoText={distributorName}
                    footerLogoIcon={footerFileUrl}
                    footerLogoText={distributorName}
                    footerText={footerLogoText}
                    darkHeaderLogoIcon={darkHeaderFileUrl}
                    darkFooterLogoIcon={darkFooterFileUrl}
                    handleClose={() => {
                        setPreviewModal(false);
                    }}
                    handleContinue={() => {
                        setBtnLoader(true);
                        setSuccessModal(true);
                    }}
                />
            }

            {successModal === true &&
                <SuccessModal
                    handleClose={() => {
                        setSuccessModal(false);
                        setPreviewModal(false);
                    }}
                    handleBack={() => {
                        setSuccessModal(false);
                        navigate('/white-label-settings');
                    }}
                />
            }
        </>
    )
}
